/**
 * Builder for callout elements
 */
import { CalloutElementBuilderInterface } from '../element-interfaces';
import { BaseBuilder } from './BaseBuilder';
import { BlockElement } from '../../BlockRenderer/types';
import { deepClone } from '../utils';

/**
 * Builder for creating callout elements
 * 
 * @example
 * ```typescript
 * const callout = Elements.Callout()
 *   .type('info')
 *   .title('Important Information')
 *   .addElement(Elements.Text().text('This is important information.').build())
 *   .build();
 * ```
 */
export class CalloutElementBuilder extends BaseBuilder<BlockElement, CalloutElementBuilderInterface> implements CalloutElementBuilderInterface {
  /**
   * Creates a new CalloutElementBuilder instance
   */
  constructor() {
    super({
      type: 'callout',
      variant: 'info', // Default variant
      elements: []
    });
  }

  /**
   * Creates a new CalloutElementBuilder instance
   */
  static builder(): CalloutElementBuilderInterface {
    return new CalloutElementBuilder();
  }

  /**
   * Sets the callout variant
   * 
   * @param variant - Variant of callout (info, success, warning, error, note)
   */
  variant(variant: 'info' | 'success' | 'warning' | 'error' | 'note'): CalloutElementBuilderInterface {
    const copy = this.copy();
    copy.data.variant = variant;
    return copy;
  }
  
  /**
   * Sets the color for the callout
   * 
   * @param color - Color to use for the callout
   */
  color(color: string): CalloutElementBuilderInterface {
    const copy = this.copy();
    copy.data.color = color;
    return copy;
  }

  /**
   * Sets the icon for the callout
   * 
   * @param icon - Icon name to use
   */
  icon(icon: string): CalloutElementBuilderInterface {
    const copy = this.copy();
    copy.data.icon = icon;
    return copy;
  }

  /**
   * Sets the title for the callout
   * 
   * @param title - Title text
   */
  title(title: string): CalloutElementBuilderInterface {
    const copy = this.copy();
    copy.data.title = title;
    return copy;
  }

  /**
   * Adds an element to the callout
   * 
   * @param element - Element to add to the callout
   */
  addElement(element: BlockElement | { build(): BlockElement }): CalloutElementBuilderInterface {
    const copy = this.copy();
    const elementJson = 'build' in element ? element.build() : deepClone(element);
    copy.data.elements = [...(copy.data.elements as BlockElement[] || []), elementJson];
    return copy;
  }

  /**
   * Builds and returns the final callout element
   */
  build(): BlockElement {
    if (!this.data.elements || (this.data.elements as BlockElement[]).length === 0) {
      throw new Error('Callout must have at least one element');
    }

    return {
      type: 'callout',
      variant: this.data.variant as 'info' | 'success' | 'warning' | 'error' | 'note',
      ...(this.data.icon ? { icon: this.data.icon } : {}),
      ...(this.data.title ? { title: this.data.title } : {}),
      ...(this.data.color ? { color: this.data.color } : {}),
      elements: this.data.elements as BlockElement[]
    };
  }
}
